/**
 * WEB-24: тумблер «Уведомления» — один на оба экрана (участник и владелец).
 *
 * 🔴 Разрешение на уведомления браузер даёт ТОЛЬКО в ответ на клик человека:
 * само оно не спросится, и «включить за него» нельзя ни при какой настройке.
 * Поэтому кнопка, а не автоматика.
 *
 * Два вида одного и того же:
 *  - `line` — строка подвала у участника: кнопка и объяснение рядом. Там есть
 *    место, и там же живут «Защита переписки» и «Копия и перенос».
 *  - `header` — компактная кнопка в шапке кабинета, на месте прежней «Включить
 *    уведомления» (WEB-10). Та лишь спрашивала разрешение и работала, пока
 *    вкладка открыта; теперь тем же нажатием оформляется настоящая подписка.
 *
 * Объяснение отказа обязательно и разное (см. `pushClient.pushHint`): «браузер
 * не умеет» на айфоне — это ложь, там нужен экран «Домой».
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { browserDeviceId } from './deviceId';
import { MsgApi } from './msgApi';
import {
  PUSH_NEEDS_HOME_SCREEN,
  PUSH_NEEDS_SAFARI,
  PUSH_NOT_CONFIGURED,
  PUSH_ON,
  PUSH_UNSUPPORTED,
  disablePush,
  enablePush,
  pushHint,
  pushState,
  resyncPush,
} from './pushClient';
import { logger } from '../utils/logger';

// Состояния, в которых нажатие ничего изменить не может: кнопка гаснет, но
// объяснение остаётся — оно здесь и есть ответ («добавьте на экран Домой»).
const HOPELESS = [
  PUSH_UNSUPPORTED, PUSH_NEEDS_HOME_SCREEN, PUSH_NEEDS_SAFARI,
  PUSH_NOT_CONFIGURED,
];

/**
 * 🔴 Состояние ОБЩЕЕ на все экземпляры тумблера (ревью 09.09).
 *
 * После WEB-25 их на экране два, и оба смонтированы одновременно: кнопка в
 * шапке кабинета и строка в боковом меню (`Aside` живёт в каркасе постоянно).
 * Со своим состоянием у каждого включение в шапке оставляло бы в меню надпись
 * «Включить уведомления», и повторное нажатие там звало бы `enablePush`
 * вместо выключения — тумблер врал бы о том, что уже сделано.
 *
 * Засов `busy` тоже общий и синхронный. Он заводился против двойного тапа в
 * одном тике (`setBusy` применяется асинхронно); с двумя экземплярами «двойной
 * тап» бывает ещё и по разным кнопкам сразу.
 */
const subscribers = new Set();
let sharedState = null;
let sharedBusy = false;
let inFlight = null;      // идущее чтение состояния браузера
let toggleInFlight = null; // идущее включение/выключение
/**
 * 🔴 Поколение: растёт при КАЖДОМ полном размонтировании (ревью 09.09).
 *
 * Без него запоздалый ответ прежнего экземпляра садился в общий кэш уже
 * ПОСЛЕ того, как человек размонтировал строку, смонтировал заново и включил
 * уведомления: прочитанное когда-то `off` затирало свежее `on`, и следующее
 * нажатие подписывало второй раз вместо выключения. Проверка `subscribers.size`
 * этого не ловит — после перемонтирования подписчик снова есть, просто ДРУГОЙ.
 * Перемонтирование достижимо обычным «Ещё» → «Свернуть» у участника.
 */
let generation = 0;

function notify() {
  subscribers.forEach((fn) => fn(sharedState, sharedBusy));
}

function setSharedState(next) { sharedState = next; notify(); }
function setSharedBusy(next) { sharedBusy = next; notify(); }

const PushToggle = ({ api = null, variant = 'line', testId = 'push-toggle' }) => {
  const [state, setState] = useState(sharedState);
  const [busy, setBusy] = useState(sharedBusy);
  const alive = useRef(true);

  const client = useCallback(
    () => api || new MsgApi(browserDeviceId()), [api],
  );

  useEffect(() => {
    alive.current = true;
    const listener = (s, b) => {
      if (!alive.current) return;
      setState(s);
      setBusy(b);
    };
    subscribers.add(listener);
    // Спрашиваем браузер ОДИН раз на все экземпляры: два запроса подряд дали бы
    // и два `resyncPush` на сервер.
    if (sharedState === null && inFlight === null) {
      const myGen = generation;
      // Не спрашиваем браузер, пока не договорило переключение ЭТОГО же
      // поколения: посреди подписки он честно ответит «выключено», и мы
      // предложили бы включить второй раз. Операции прошлых поколений здесь
      // уже забыты (см. размен в очистке ниже) — ждать их нечем и незачем.
      inFlight = Promise.resolve(toggleInFlight).catch(() => {}).then(pushState)
        .then((s) => {
        // Ответ протух: пока читали, всё размонтировали. Класть его в кэш
        // нельзя — там уже может лежать результат нового включения.
        if (generation !== myGen) return;
        inFlight = null;
        // Все экземпляры успели уйти — состояние не запоминаем, иначе
        // следующий монтаж получил бы протухшее без перепроверки.
        if (subscribers.size === 0) return;
        setSharedState(s);
        // Подписка в браузере есть — пересказываем её серверу. Он мог погасить
        // её при ревокации устройства (Д-2, Д-20, Д-3), и тогда уведомления
        // замолкли бы молча, а тумблер продолжал бы показывать «включено».
        if (s === PUSH_ON) resyncPush(client());
      });
    }
    return () => {
      alive.current = false;
      subscribers.delete(listener);
      // Экземпляров не осталось — забываем: подписку могли отозвать снаружи,
      // и следующий монтаж обязан спросить браузер заново.
      if (subscribers.size === 0) {
        generation += 1;
        sharedState = null;
        inFlight = null;
        /**
         * 🔴 Размен, сделанный сознательно (второе ревью 09.09).
         *
         * Сначала засов здесь переживал размонтирование — чтобы новый монтаж
         * не запустил второе включение поверх первого. Оказалось хуже болезни:
         * запрос, который не отвечает ВООБЩЕ, не доходит до `finally`, засов
         * держится вечно, и после перемонтирования кнопка исчезает совсем —
         * состояние остаётся `null`. Перемонтирование до этого было
         * единственным способом ожить без перезагрузки страницы, и мы его
         * отнимали.
         *
         * Поэтому забываем операцию целиком. Двойное включение при этом
         * возможно, но ограничено: подписка уникальна по устройству
         * (`uq_msg_push_subs_active`), а сорванный кэш уже прикрыт поколением.
         * Мёртвая кнопка не лечится ничем, кроме перезагрузки, — это дороже.
         */
        sharedBusy = false;
        toggleInFlight = null;
      }
    };
  }, [client]);

  const toggle = useCallback(async () => {
    // Засов читается синхронно из общей переменной — именно это и защищает от
    // двойного нажатия в одном тике, в том числе по двум разным экземплярам.
    if (sharedBusy) return;
    const myGen = generation;
    setSharedBusy(true);
    const run = (async () => {
      try {
        return sharedState === PUSH_ON
          ? await disablePush(client())
          : await enablePush(client());
      } catch (e) {
        // Наружу не выпускаем: сорванная подписка не имеет права уронить чат.
        logger.warn('push: переключение не удалось', e);
        return pushState();
      }
    })();
    toggleInFlight = run;
    try {
      const next = await run;
      // 🔴 Пока переключали, все экземпляры могли уйти, и кэш уже очищен.
      // Вернуть туда результат — значит выдать следующему монтажу «включено»
      // без единого вопроса браузеру, пропустив и сверку с сервером.
      if (generation === myGen) setSharedState(next);
    } finally {
      // 🔴 Занятость снимает ТОЛЬКО та операция, которая её поставила (третье
      // ревью 09.09). Безусловный сброс открывал кнопку, когда договаривал
      // СТАРЫЙ заход, а новый ещё шёл, — и следующее нажатие запускало третье
      // включение параллельно второму. При полном размонтировании засов и так
      // снят в очистке, там сбрасывать нечего.
      if (toggleInFlight === run) {
        toggleInFlight = null;
        setSharedBusy(false);
      }
    }
  }, [client]);

  if (state === null) return null;           // до первого ответа — молчим
  const on = state === PUSH_ON;
  const hopeless = HOPELESS.includes(state);
  const hint = pushHint(state);

  // В шапке кабинета кнопку прячем, когда всё уже включено: висящая там
  // надпись «Выключить уведомления» — постоянный шум ради редкого действия.
  // Там же прячем и безнадёжные состояния: длинное объяснение в шапку не
  // помещается, а мёртвая кнопка хуже её отсутствия (у участника, где место
  // есть, объяснение показывается полностью).
  if (variant === 'header' && (on || hopeless)) return null;

  const label = (() => {
    if (busy) return 'Минуту…';
    if (on) return 'Выключить уведомления';
    return 'Включить уведомления';
  })();

  /**
   * WEB-25: строка в боковом меню кабинета.
   *
   * 🔴 Повод: владелец работает с телефона, а в шапке кнопка скрыта по ширине
   * (правило `@media (max-width: 640px)` из WEB-10) — и была там ЕДИНСТВЕННОЙ.
   * Включить уведомления с телефона было нельзя вовсе.
   *
   * Здесь, в отличие от шапки, места хватает — поэтому «безнадёжные»
   * состояния НЕ прячем, а показываем текстом: на iPhone это единственный
   * способ узнать про Safari и экран «Домой». Меню при нажатии не закрываем:
   * человек обязан увидеть, что состояние сменилось (в шапке подтверждением
   * служило исчезновение кнопки, здесь так нельзя).
   */
  if (variant === 'menu') {
    return (
      <div className="asidePush" data-testid={`${testId}-line`}>
        <button
          type="button"
          className="asidePushBtn"
          onClick={toggle}
          disabled={busy || hopeless}
          data-testid={testId}
          data-state={state}
          aria-pressed={on}
        >
          {label}
        </button>
        <span className="asidePushHint">{hint}</span>
      </div>
    );
  }

  if (variant === 'header') {
    return (
      <button
        type="button"
        className="cabinetUnreadAsk"
        onClick={toggle}
        disabled={busy}
        data-testid={testId}
        data-state={state}
        title={hint}
      >
        {label}
      </button>
    );
  }

  return (
    <p className="memberChatFooter" data-testid={`${testId}-line`}>
      <button
        type="button"
        className="memberChatLeave"
        onClick={toggle}
        disabled={busy || hopeless}
        data-testid={testId}
        data-state={state}
        aria-pressed={on}
      >
        {label}
      </button>
      {` — ${hint}`}
    </p>
  );
};

export default PushToggle;
